import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from 'axios';

const RoutineManager = ({ onRoutineClick, apiKey, getHeaders }) => {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState({});
  const [quickExecuteOpen, setQuickExecuteOpen] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [quickParams, setQuickParams] = useState({
    searchTerm: '',
    executionCount: 1
  });

  const apiUrl = 'http://localhost:5050';

  const fetchRoutines = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/routines`, {
        headers: getHeaders ? getHeaders() : {},
        timeout: 8000
      });
      console.log('API Response:', response.data); // Debug logging
      setRoutines(response.data.data || []);
    } catch (err) {
      console.error('Error fetching routines:', err);
      setError(`Failed to fetch routines: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutines();
  }, [apiKey]); // Re-fetch when API key changes

  const handleQuickExecute = (routine) => {
    setSelectedRoutine(routine);
    
    // Initialize parameters based on routine metadata
    const initialParams = {
      searchTerm: '',
      executionCount: 1
    };
    
    // Add dynamic parameters based on input_parameters metadata
    if (routine.input_parameters) {
      Object.keys(routine.input_parameters).forEach(paramName => {
        initialParams[paramName] = '';
      });
    }
    
    setQuickParams(initialParams);
    setQuickExecuteOpen(true);
  };

  const executeQuickRoutine = async () => {
    if (!selectedRoutine) return;

    const routineName = selectedRoutine.name;
    setExecuting(prev => ({ ...prev, [routineName]: true }));
    setQuickExecuteOpen(false);

    try {
      const executions = [];
      for (let i = 0; i < quickParams.executionCount; i++) {
        // Build parameters based on routine metadata
        const routineParams = {};
        
        if (selectedRoutine.input_parameters && Object.keys(selectedRoutine.input_parameters).length > 0) {
          // Use metadata to build parameters
          Object.keys(selectedRoutine.input_parameters).forEach(paramName => {
            const paramMeta = selectedRoutine.input_parameters[paramName];
            const userValue = quickParams[paramName];
            
            if (userValue) {
              routineParams[paramName] = userValue;
            } else if (paramMeta.default !== undefined) {
              routineParams[paramName] = paramMeta.default;
            } else if (paramMeta.required) {
              // Don't auto-fill required parameters - let the routine handle missing values
              // This ensures proper validation and error handling
              console.log(`Required parameter '${paramName}' not provided by user`);
            } else if (paramMeta.example) {
              // Only use example values for optional parameters
              routineParams[paramName] = paramMeta.example;
            }
          });
        } else {
          // Fallback for routines without metadata
          if (selectedRoutine.name === 'test_form_routine') {
            routineParams.field1 = quickParams.field1 || 'Test Value 1';
            routineParams.field2 = quickParams.field2 || 'Test Value 2';
            routineParams.field3 = quickParams.field3 || 'Test Value 3';
            routineParams.field4 = quickParams.field4 || 'Test Value 4';
          } else if (quickParams.searchTerm) {
            routineParams.search_term = quickParams.searchTerm;
          }
        }
        
        executions.push(routineParams);
      }

      // Execute all routines in parallel
      const executionPromises = executions.map(routineParams => {
        return axios.post(`${apiUrl}/api/routines/${routineName}/execute`, { params: routineParams }, {
          headers: getHeaders ? getHeaders() : {},
          timeout: 30000
        });
      });

      const results = await Promise.all(executionPromises);
      
      // Show success message
      setError(null);
      console.log('Execution results:', results);
      
    } catch (err) {
      console.error('Error executing routine:', err);
      setError(`Failed to execute routine: ${err.response?.data?.error || err.message}`);
    } finally {
      setExecuting(prev => ({ ...prev, [routineName]: false }));
    }
  };

  const handleCardClick = (routine) => {
    if (onRoutineClick) {
      onRoutineClick(routine);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading routines...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2">
          Available Routines
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchRoutines}
          disabled={loading}
        >
          Reload
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Routines Grid */}
      {routines.length === 0 ? (
        <Alert severity="info">
          No routines available. Make sure the WebOrk server is running on port 5050.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {routines.map((routine) => (
            <Grid item xs={12} sm={6} md={4} key={routine.name}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleCardClick(routine)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h3" noWrap>
                      {routine.name}
                    </Typography>
                    <Chip 
                      label={routine.class_name || 'Unknown'} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>

                  {/* Description */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, minHeight: '60px' }}
                  >
                    {routine.description || 'No description available'}
                  </Typography>

                  {/* Details */}
                  <Box sx={{ mb: 2 }}>
                    {routine.website && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Website:</strong> {routine.website}
                      </Typography>
                    )}
                    {routine.category && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Category:</strong> {routine.category}
                      </Typography>
                    )}
                    {routine.input_parameters && Object.keys(routine.input_parameters).length > 0 && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Required inputs:</strong> {Object.keys(routine.input_parameters).join(', ')}
                      </Typography>
                    )}
                    {routine.tags && routine.tags.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {routine.tags.map((tag, index) => (
                          <Chip 
                            key={index}
                            label={tag} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(routine);
                    }}
                  >
                    Details
                  </Button>
                  
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={executing[routine.name] ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickExecute(routine);
                    }}
                    disabled={executing[routine.name]}
                  >
                    {executing[routine.name] ? 'Running...' : 'Execute'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quick Execute Dialog */}
      <Dialog open={quickExecuteOpen} onClose={() => setQuickExecuteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Quick Execute: {selectedRoutine?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {/* Dynamic parameter generation based on routine metadata */}
            {selectedRoutine?.input_parameters && Object.keys(selectedRoutine.input_parameters).length > 0 ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {selectedRoutine.name} Parameters
                </Typography>
                {Object.entries(selectedRoutine.input_parameters).map(([paramName, paramMeta]) => (
                  <TextField
                    key={paramName}
                    fullWidth
                    label={`${paramName}${paramMeta.required ? ' *' : ''}`}
                    placeholder={paramMeta.example || `Enter ${paramName}...`}
                    helperText={paramMeta.description}
                    value={quickParams[paramName] || ''}
                    onChange={(e) => setQuickParams(prev => ({ ...prev, [paramName]: e.target.value }))}
                    sx={{ mb: 2 }}
                    required={paramMeta.required}
                  />
                ))}
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedRoutine.input_parameters && Object.values(selectedRoutine.input_parameters).some(p => p.required) 
                    ? 'Fields marked with * are required. Empty fields will use default values.'
                    : 'All fields are optional. Empty fields will use default values.'
                  }
                </Alert>
              </>
            ) : (
              <>
                {/* Fallback for routines without metadata */}
                <TextField
                  fullWidth
                  label="Search Term (optional)"
                  placeholder="Enter search term..."
                  value={quickParams.searchTerm}
                  onChange={(e) => setQuickParams(prev => ({ ...prev, searchTerm: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Number of Executions</InputLabel>
              <Select
                value={quickParams.executionCount}
                onChange={(e) => setQuickParams(prev => ({ ...prev, executionCount: e.target.value }))}
                label="Number of Executions"
              >
                {[1, 2, 3, 4, 5, 10].map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary">
              This will execute the routine {quickParams.executionCount} time{quickParams.executionCount > 1 ? 's' : ''}
              {selectedRoutine?.input_parameters && Object.keys(selectedRoutine.input_parameters).length > 0 ? 
                ' with the provided parameters' : 
                (quickParams.searchTerm && ` with search term "${quickParams.searchTerm}"`)
              }.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickExecuteOpen(false)}>
            Cancel
          </Button>
          <Button onClick={executeQuickRoutine} variant="contained">
            Execute
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoutineManager;
